import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { TOP_UP_BUNDLES } from '../../lib/top-up-bundles';
import { useTopUpPointsMutation } from './mutations';
import { TopUpBundleCard } from './TopUpBundleCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const BUNDLE_BADGES: Record<string, string> = {
  value: 'Most Popular',
  ultimate: 'Best Value',
};

export interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TopUpModal({ isOpen, onClose }: TopUpModalProps): JSX.Element {
  const mutation = useTopUpPointsMutation();
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);
  const [bundleKeys, setBundleKeys] = useState<Map<string, string>>(() => new Map());
  const inFlightRef = useRef(false);

  const handleSelect = useCallback(
    (bundleId: string) => {
      if (inFlightRef.current) return;
      let key = bundleKeys.get(bundleId);
      if (!key) {
        key = crypto.randomUUID();
        const next = new Map(bundleKeys);
        next.set(bundleId, key);
        setBundleKeys(next);
      }
      setSelectedBundleId(bundleId);
      inFlightRef.current = true;
      mutation.mutate(
        { bundle_id: bundleId, idempotency_key: key },
        {
          onError: (err) => {
            inFlightRef.current = false;
            toast.error(err.message);
          },
          onSuccess: (result) => {
            inFlightRef.current = false;
            setSelectedBundleId(null);
            toast.success(
              `Added ${result.points_added.toLocaleString()} points! New balance: ${result.new_balance.toLocaleString()}`,
            );
            setBundleKeys((prev) => {
              if (!prev.has(bundleId)) return prev;
              const next = new Map(prev);
              next.delete(bundleId);
              return next;
            });
          },
        },
      );
    },
    [mutation, bundleKeys],
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent data-testid="topup-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Top Up Points</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {TOP_UP_BUNDLES.map((bundle) => {
            const isActive = selectedBundleId === bundle.id;
            return (
              <TopUpBundleCard
                key={bundle.id}
                bundle={bundle}
                onSelect={handleSelect}
                isPending={isActive && mutation.isPending}
                badge={BUNDLE_BADGES[bundle.id]}
                disabled={mutation.isPending && !isActive}
              />
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
